import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box, Card, CardContent, CardHeader } from "@material-ui/core";
import { map } from "ramda";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import { isBlank, notBlank, toKeyValue } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { makeStyles } from "@material-ui/core/styles";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import {
  useOrganizationProperties,
  useOrganizationPropertyDelete,
} from "../organizationServices";
import OrganizationPropertyDialog from "../edit/OrganizationPropertyDialog";
import { AlertContext } from "../../components/alert/AlertProvider";
import ConfirmButton from "../../components/ConfirmButton";
import { caseInsensitiveSort } from "../../util/sorts";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },

  cell: {
    minWidth: 100,
  },
}));

const OrganizationPropertiesCard = ({ id }) => {
  const classes = useStyles();
  const { alertError } = useContext(AlertContext);
  const [properties, setProperties] = useState([]);

  const propsResponse = useOrganizationProperties(id);
  useEffect(() => {
    if (propsResponse.isSuccess && notBlank(propsResponse.data)) {
      setProperties(toKeyValue(propsResponse.data));
    } else {
      setProperties([]);
    }
  }, [propsResponse.isSuccess, propsResponse.data]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const handleAddClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handlePropertyCancel = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handlePropertySave = useCallback(
    async data => {
      await propsResponse.refetch();
      setDialogOpen(false);
    },
    [propsResponse],
  );

  const propertyDelete = useOrganizationPropertyDelete();
  const handleDeleteClick = useCallback(
    async key => {
      try {
        await propertyDelete.mutateAsync({
          resourceIds: { orgId: id, propKey: key },
        });
        await propsResponse.refetch();
      } catch (err) {
        alertError(err);
      }
    },
    [id, propertyDelete, propsResponse, alertError],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Properties"
        action={
          <Tooltip title="Add Property">
            <IconButton onClick={handleAddClick}>
              <AddIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {propsResponse.isLoading && <PanelLoadingIndicator />}
        {propsResponse.isError && (
          <Box padding={5} color="error" fontSize="1.4rem" fontStyle="italic">
            Error loading Organization Properties.
          </Box>
        )}
        {propsResponse.isSuccess && isBlank(properties) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Properties set for this Organization.
          </Box>
        )}
        {propsResponse.isSuccess && notBlank(properties) && (
          <TableContainer>
            <Table stickyHeader aria-label="Properties">
              <TableHead>
                <TableRow>
                  <TableCell
                    key="prop-key"
                    align="left"
                    className={classes.cell}
                  >
                    Key
                  </TableCell>

                  <TableCell
                    key="prop-value"
                    align="left"
                    className={classes.cell}
                  >
                    Value
                  </TableCell>

                  <TableCell
                    key="actions"
                    align="center"
                    style={{ width: 50 }}
                  />
                </TableRow>
              </TableHead>

              <TableBody>
                {map(
                  prop => (
                    <TableRow hover tabIndex={-1} key={prop.key}>
                      <TableCell
                        key={"prop-key:" + prop.key}
                        align="left"
                        className={classes.cell}
                      >
                        {prop.key}
                      </TableCell>

                      <TableCell
                        key={"prop-value:" + prop.key}
                        align="left"
                        className={classes.cell}
                      >
                        {prop.value === "0"
                          ? "false"
                          : prop.value === "1"
                          ? "true"
                          : prop.value}
                      </TableCell>

                      <TableCell
                        key={"actions:" + prop.key}
                        align="center"
                        style={{ width: 50 }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          pl={4}
                        >
                          <ConfirmButton
                            handleDelete={() => handleDeleteClick(prop.key)}
                            dialogTitle="Remove Property"
                            dialogBody="Are you sure you want to remove this property?"
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ),
                  caseInsensitiveSort("key")(properties),
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {dialogOpen && (
        <OrganizationPropertyDialog
          organizationId={id}
          open={dialogOpen}
          onCancel={handlePropertyCancel}
          onSave={handlePropertySave}
        />
      )}
    </Card>
  );
};

export default OrganizationPropertiesCard;
